import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AnnotationsContext from '../context/AnnotationsContext';
import CommentsAPI from '../api/CommentsAPI';
import FavoritesAPI from '../api/FavoritesAPI';
import { CommentModel } from '../types/CommentModel';

// Loads the game's comment thread once and holds the favorite-move set, exposing mutators the
// move list, per-move overlay and game-level thread all share.
export default function AnnotationsProvider(props: {
  gameId: number;
  initialFavorites: string[];
  children: React.ReactNode;
}): React.ReactNode {
  const { gameId, initialFavorites, children } = props;
  const [comments, setComments] = useState<CommentModel[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set(initialFavorites));
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelClosing, setPanelClosing] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pending comment-box focus request (move PGN, or null for the game thread). The bumping nonce
  // re-fires the composer's consume effect even when the target move is already selected.
  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  const pendingFocusRef = useRef<{ movePgn: string | null } | null>(null);

  // Desktop keeps the panel docked permanently; these only drive the mobile bottom sheet. Close
  // runs a short slide-down animation (panelClosing) before actually unmounting the sheet.
  const openPanel = useCallback(() => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setPanelClosing(false);
    setPanelOpen(true);
  }, []);
  const closePanel = useCallback(() => {
    setPanelClosing(true);
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
    }
    closeTimer.current = setTimeout(() => {
      setPanelOpen(false);
      setPanelClosing(false);
      closeTimer.current = null;
    }, 240);
  }, []);

  const requestComposerFocus = useCallback((movePgn: string | null) => {
    pendingFocusRef.current = { movePgn };
    setComposerFocusNonce((n) => n + 1);
  }, []);
  // True (once) if a focus request is pending for this move; clears it so navigation doesn't re-focus.
  const consumeComposerFocus = useCallback((movePgn: string | null) => {
    if (pendingFocusRef.current != null && pendingFocusRef.current.movePgn === movePgn) {
      pendingFocusRef.current = null;
      return true;
    }
    return false;
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CommentsAPI.getThread(gameId);
      setComments(data ?? []);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const upsertComment = useCallback((comment: CommentModel) => {
    setComments((prev) => {
      const idx = prev.findIndex((c) => c.Id === comment.Id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = comment;
        return copy;
      }
      return [...prev, comment];
    });
  }, []);

  const removeComment = useCallback((id: number) => {
    setComments((prev) => prev.filter((c) => c.Id !== id));
  }, []);

  const toggleFavorite = useCallback(
    async (movePgn: string) => {
      const next = !favorites.has(movePgn);
      // Optimistic; revert on failure.
      setFavorites((prev) => {
        const updated = new Set(prev);
        if (next) {
          updated.add(movePgn);
        } else {
          updated.delete(movePgn);
        }
        return updated;
      });
      try {
        await FavoritesAPI.toggleFavoriteMove(gameId, movePgn, next);
      } catch {
        setFavorites((prev) => {
          const updated = new Set(prev);
          if (next) {
            updated.delete(movePgn);
          } else {
            updated.add(movePgn);
          }
          return updated;
        });
      }
    },
    [favorites, gameId],
  );

  const value = useMemo(
    () => ({
      comments,
      favorites,
      loading,
      reload,
      upsertComment,
      removeComment,
      toggleFavorite,
      panelOpen,
      panelClosing,
      openPanel,
      closePanel,
      composerFocusNonce,
      requestComposerFocus,
      consumeComposerFocus,
    }),
    [
      comments,
      favorites,
      loading,
      reload,
      upsertComment,
      removeComment,
      toggleFavorite,
      panelOpen,
      panelClosing,
      openPanel,
      closePanel,
      composerFocusNonce,
      requestComposerFocus,
      consumeComposerFocus,
    ],
  );

  return <AnnotationsContext.Provider value={value}>{children}</AnnotationsContext.Provider>;
}
